import { useState, useEffect, useRef, useCallback } from "react";
import { useStableCallback } from "@/hooks/useStableCallback";
import axios from "axios";
import { invalidateApiCache } from "@/lib/apiCache";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Navbar from "@/components/Navbar";
import PaymentDialog from "@/components/PaymentDialog";
import { CustomerDialog } from "@/components/customers/CustomerDialog";
import { CustomerSearchDialog, AgeCheckDialog as CustomerAgeCheckDialog } from "@/components/customers/PosCustomerDialogs";
import ButtonConfigDialog from "@/components/ButtonConfigDialog";
import AgeCheckDialog from "@/components/AgeCheckDialog";
import NumpadModal from "@/components/NumpadModal";
import VendorPayoutDialog from "@/components/VendorPayoutDialog";
import CartPanel from "@/components/pos/CartPanel";
import ProductGrid from "@/components/pos/ProductGrid";
import QuickAddDialog from "@/components/pos/QuickAddDialog";
import LookupDialog from "@/components/pos/LookupDialog";
import PriceCheckDialog from "@/components/pos/PriceCheckDialog";
import SubButtonPopup from "@/components/pos/SubButtonPopup";
import { toTitleCase } from "@/utils/titleCase";
import { usePrinting } from "@/hooks/usePrinting";
import { useScanFocusGuard } from "@/hooks/useScanFocusGuard";
import { playScanFeedback } from "@/lib/scanSounds";
import { Search, Trash2, ScanBarcode, Tag, Pause, Play, XCircle, RotateCcw, DollarSign, Printer, Clock, Loader2, Truck, Plus, AlertTriangle, Pencil, Maximize, Minimize } from "lucide-react";
import { API } from "@/apiConfig";
import { fetchTerminalSettings } from "@/lib/terminal";
import { syncReceiptLogoCache } from "@/lib/printServer";

export default function POSScreen({ employee, onLogout, station }) {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDlg, setCustomerDlg] = useState(null); // "add" | "search" | "age"
  const [quickPayType, setQuickPayType] = useState(null);
  const [paxSettings, setPaxSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [displaySettings, setDisplaySettings] = useState({ 
    cart_position: "right",
    show_quick_pay_buttons: true,
    // Area Colors - Background & Border
    product_grid_bg_color: "#FFFFFF",
    product_grid_border_color: "#E4E4E7",
    tabs_bg_color: "#FFFFFF",
    tabs_border_color: "#E4E4E7",
    cart_bg_color: "#FFFFFF",
    cart_border_color: "#E4E4E7",
    scan_bg_color: "#FFFFFF",
    scan_border_color: "#E4E4E7",
    bottom_buttons_bg_color: "#FFFFFF",
    bottom_buttons_border_color: "#E4E4E7",
    top_header_bg_color: "#FFFFFF",
    top_header_border_color: "#E4E4E7"
  });
  const [storeSettings, setStoreSettings] = useState(null);
  const [buttonPositions, setButtonPositions] = useState([]);
  const [showButtonConfig, setShowButtonConfig] = useState(false);
  const [configPosition, setConfigPosition] = useState(null);
  const [configProduct, setConfigProduct] = useState(null);
  const [configDisplayName, setConfigDisplayName] = useState(null);
  const [configCardStyle, setConfigCardStyle] = useState(null);
  const [configBorderColor, setConfigBorderColor] = useState(null);
  const [configSubButtons, setConfigSubButtons] = useState(null);
  const [configButtonImage, setConfigButtonImage] = useState(null);
  const [configSubButtonsImage, setConfigSubButtonsImage] = useState(null);
  const [subButtonPopup, setSubButtonPopup] = useState(null); // {parentProduct, subButtons, position}
  
  // Quick Sale States (using categories with show_on_pos)
  const [quickSaleCategories, setQuickSaleCategories] = useState([]);
  const [showNumpad, setShowNumpad] = useState(false);
  const [selectedQuickSale, setSelectedQuickSale] = useState(null);
  
  // Scan/Search bar
  const [scanInput, setScanInput] = useState("");
  const scanInputRef = useRef(null);
  useScanFocusGuard(scanInputRef);
  const cartEndRef = useRef(null);
  
  // Mix & Match discount state
  const [mixMatchDiscount, setMixMatchDiscount] = useState({ applied_discounts: [], total_savings: 0 });

  // Auto-scroll cart to bottom when items are added
  useEffect(() => {
    if (cart.length > 0) {
      setTimeout(() => cartEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    }
  }, [cart.length]);

  // Buydown state
  const [buydownSavings, setBuydownSavings] = useState(0);
  const [buydownDetails, setBuydownDetails] = useState([]);

  // POS Action States
  const [heldTransactions, setHeldTransactions] = useState([]);
  const [isRefundMode, setIsRefundMode] = useState(false);
  const [originalRefundTxn, setOriginalRefundTxn] = useState("");
  const [originalRefundData, setOriginalRefundData] = useState(null);
  const [showHoldDialog, setShowHoldDialog] = useState(false);
  
  // Price Change States
  const [selectedCartItemId, setSelectedCartItemId] = useState(null);
  const [showPriceChangeDialog, setShowPriceChangeDialog] = useState(false);
  const [newPriceInput, setNewPriceInput] = useState("");
  
  // Transaction Complete State
  const [transactionComplete, setTransactionComplete] = useState(false);
  const [completedPaymentMethod, setCompletedPaymentMethod] = useState(null); // 'cash' or 'card'
  const [completedAmountTendered, setCompletedAmountTendered] = useState(0);
  const [completedChangeDue, setCompletedChangeDue] = useState(0);
  // Cash tendered/change published to the customer display while the POS change popup is still up
  const [pendingChange, setPendingChange] = useState(null);
  
  // Vendor Payout State
  const [showVendorPayoutDialog, setShowVendorPayoutDialog] = useState(false);
  
  // Hardware Settings State (for customer display ad images)
  const [hardwareSettings, setHardwareSettings] = useState(null);
  
  // Initialize printing hook
  const { 
    isPrinting, 
    isOpeningDrawer, 
    handleTransactionComplete: handlePrint, 
    printLastReceipt, 
    openCashDrawer,
    hasLastTransaction 
  } = usePrinting(hardwareSettings);
  
  // Ask For Price State
  const [showPriceInput, setShowPriceInput] = useState(false);
  const [priceInputProduct, setPriceInputProduct] = useState(null);

  // --- Age Verification State ---
  // Session-level: reset on transaction complete or clearCart
  const [verifiedAge, setVerifiedAge] = useState(null);            // highest verified age this txn
  const [verifiedCustomer, setVerifiedCustomer] = useState(null);  // { name, dob, method, state, license_no_last4 }
  const [ageVerifications, setAgeVerifications] = useState([]);    // audit entries
  const [ageSkipEvents, setAgeSkipEvents] = useState([]);          // audit entries
  const [ageCheckOpen, setAgeCheckOpen] = useState(false);
  const [ageCheckRequired, setAgeCheckRequired] = useState(21);
  const [pendingAgeProduct, setPendingAgeProduct] = useState(null); // product waiting to be added

  // Fullscreen State
  const [isFullscreen, setIsFullscreen] = useState(false);

  // --- Cart must never survive a reload/close: block logout with items in cart,
  // and never restore a cart from a previous session (crash/close is treated the same). ---
  const handleLogoutClick = useCallback(() => {
    // A completed/approved sale is just a receipt still on screen — it may be left safely.
    if (cart.length > 0 && !transactionComplete) {
      toast.error("Cart must be empty before logging out. Complete or clear the sale first.");
      return;
    }
    onLogout();
  }, [cart, transactionComplete, onLogout]);

  // --- Fullscreen: auto-enter on POS mount, track state ---
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    // Auto-enter fullscreen on POS mount (requires prior user gesture)
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Register heartbeat — keeps the lock alive every 60 seconds
  useEffect(() => {
    const stationId = station?.station_id || localStorage.getItem("station_id");
    if (!stationId) return;

    const sendHeartbeat = () => {
      const token = localStorage.getItem("token");
      fetch(`${API}/registers/heartbeat?station_id=${stationId}`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).catch(() => {});
    };

    // Send immediately, then every 60 seconds
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(interval);
  }, [station]);

  // Item Not Found State
  const [showItemNotFound, setShowItemNotFound] = useState(false);
  const [notFoundSku, setNotFoundSku] = useState("");

  // Error sound for item not found
  const playErrorSound = () => playScanFeedback(storeSettings, 'error');
  const playSuccessSound = () => playScanFeedback(storeSettings, 'success');
  
  // Quick Add Item State
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [showLookup, setShowLookup] = useState(false);
  const [showPriceCheck, setShowPriceCheck] = useState(false);
  const [quickAddForm, setQuickAddForm] = useState({
    sku: "",
    name: "",
    cost_price: "",
    retail_price: "",
    gp_percent: "",
    category: "",
    sub_category: "",
    group: "",
    mfg: "",
    vendor_ids: [],
    units_per_case: "",
    tax_1: false,
    tax_2: false,
    foodstamp: false
  });
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [vendors, setVendors] = useState([]);

  const fetchHardwareSettings = useStableCallback(async () => {
    try {
      const stationId = station?.station_id || localStorage.getItem("station_id") || "default";
      const response = await axios.get(`${API}/settings/hardware?station_id=${stationId}`);
      // If station-specific settings are bare (no printer configured), try "default" as fallback
      if (!response.data?.receipt_printer_name && stationId !== "default") {
        const fallback = await axios.get(`${API}/settings/hardware?station_id=default`);
        if (fallback.data?.receipt_printer_name) {
          setHardwareSettings(fallback.data);
          syncReceiptLogoCache(fallback.data);
          return;
        }
      }
      setHardwareSettings(response.data);
      syncReceiptLogoCache(response.data);
    } catch (error) {
      console.error("Failed to load hardware settings", error);
    }
  });

  useEffect(() => {
    fetchProducts();
    fetchTabs();
    fetchDisplaySettings();
    fetchStoreSettings();
    fetchPaxSettings();
    fetchQuickSaleCategories();
    fetchHardwareSettings();
    fetchQuickAddData();
    // Focus scan input on mount
    setTimeout(() => {
      scanInputRef.current?.focus();
    }, 500);
  }, [fetchHardwareSettings]);

  // Auto-refresh the product list when mobile-app catalog edits land locally (every 5s),
  // plus whenever the window regains focus — prices/new items show up without a re-login.
  const catalogVersionRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    const checkCatalogVersion = async (forceRefresh = false) => {
      try {
        const res = await axios.get(`${API}/products/catalog-version`);
        const version = res.data?.version ?? 0;
        if (cancelled) return;
        const changed = catalogVersionRef.current !== null && version !== catalogVersionRef.current;
        catalogVersionRef.current = version;
        if (changed || forceRefresh) {
          invalidateApiCache("products");
          invalidateApiCache("categories");
          fetchProducts();
          fetchQuickAddData();
        }
      } catch (e) {
        if (forceRefresh) fetchProducts();
      }
    };
    checkCatalogVersion();
    const interval = setInterval(checkCatalogVersion, 15000);
    const onFocus = () => checkCatalogVersion(true);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, []); // eslint-disable-line

  // Re-fetch display settings when window regains focus (e.g., returning from Settings page)
  useEffect(() => {
    const handleFocus = () => { fetchDisplaySettings(); };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []); // eslint-disable-line

  // Re-fetch hardware settings when station changes (e.g., after login + station selection)
  useEffect(() => {
    if (station?.station_id) {
      fetchHardwareSettings();
    }
  }, [station]); // eslint-disable-line

  // Always refocus scan input after any click on the page
  useEffect(() => {
    const refocusScanInput = (e) => {
      // Don't refocus if clicking on specific interactive elements that need focus
      const target = e.target;
      const isInteractive = target.closest('button') || 
                           target.closest('a') || 
                           target.closest('[role="dialog"]') ||
                           target.closest('.payment-dialog') ||
                           target.tagName === 'INPUT' ||
                           target.tagName === 'SELECT' ||
                           target.tagName === 'TEXTAREA';
      
      // If not an interactive element that needs its own focus, refocus scan input
      if (!isInteractive) {
        setTimeout(() => {
          scanInputRef.current?.focus();
        }, 100);
      }
    };

    // Refocus after any click
    document.addEventListener('click', refocusScanInput);
    
    return () => {
      document.removeEventListener('click', refocusScanInput);
    };
  }, []);

  const fetchButtonPositions = useStableCallback(async () => {
    if (!activeTab) return;
    try {
      const response = await axios.get(`${API}/button-positions/${activeTab}`);
      setButtonPositions(response.data);
    } catch (error) {
      console.error("Failed to load button positions", error);
    }
  });

  useEffect(() => {
    if (activeTab) {
      fetchButtonPositions();
    }
  }, [activeTab, fetchButtonPositions]);

  // Calculate Mix & Match discounts when cart changes
  useEffect(() => {
    const calculateMixMatchDiscounts = async () => {
      if (cart.length === 0) {
        setMixMatchDiscount({ applied_discounts: [], total_savings: 0 });
        return;
      }
      
      try {
        // Build cart items for calculation
        const cartItemsForCalc = cart.map(item => {
          // For quick sale items, use item properties directly
          if (item.is_quick_sale) {
            return {
              product_id: item.product_id,
              sku: "",
              category: "",
              sub_category: "",
              group: "",
              price: item.price,
              quantity: item.quantity,
            };
          }
          
          // For regular items, look up product details
          const product = products.find(p => p.id === item.product_id);
          return {
            product_id: item.product_id,
            sku: product?.sku || "",
            category: product?.category || "",
            sub_category: product?.sub_category || "",
            group: product?.group || "",
            price: item.price,
            quantity: item.quantity,
          };
        });
        
        const response = await axios.post(`${API}/mix-match/calculate`, {
          cart_items: cartItemsForCalc,
        });
        
        setMixMatchDiscount(response.data);
      } catch (error) {
        console.error("Failed to calculate mix & match discounts", error);
        setMixMatchDiscount({ applied_discounts: [], total_savings: 0 });
      }
    };
    
    calculateMixMatchDiscounts();
  }, [cart, products]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTabs = async () => {
    try {
      const response = await axios.get(`${API}/pos-tabs`);
      const fetchedTabs = response.data;
      setTabs(fetchedTabs);
      if (fetchedTabs.length > 0) {
        setActiveTab(fetchedTabs[0].id);
      } else {
        // No tabs exist - use default tab ID so buttons can still be assigned
        setActiveTab("default");
      }
    } catch (error) {
      console.error("Failed to load tabs", error);
      setActiveTab("default");
    }
  };

  const fetchDisplaySettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/display`);
      setDisplaySettings(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error("Failed to load display settings", error);
    }
  };

  const fetchPaxSettings = async () => {
    try {
      const stationId = localStorage.getItem("station_id") || "default";
      setPaxSettings(await fetchTerminalSettings(stationId));
    } catch (error) {
      console.error("Failed to load card terminal settings", error);
    }
  };

  const fetchStoreSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/store`);
      setStoreSettings(response.data);
    } catch (error) {
      console.error("Failed to load store settings", error);
    }
  };


  const fetchQuickSaleCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories/pos`);
      setQuickSaleCategories(response.data);
    } catch (error) {
      console.error("Failed to load POS categories", error);
    }
  };

  // Fetch master data for Quick Add modal
  const fetchQuickAddData = async () => {
    try {
      const [catRes, subCatRes, groupRes, mfgRes, vendorRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/sub-categories`),
        axios.get(`${API}/groups`),
        axios.get(`${API}/manufacturers`),
        axios.get(`${API}/vendors`)
      ]);
      setCategories(catRes.data);
      setSubCategories(subCatRes.data);
      setGroups(groupRes.data);
      setManufacturers(mfgRes.data);
      setVendors(vendorRes.data);
    } catch (error) {
      console.error("Failed to fetch quick add data", error);
    }
  };

  // Open Quick Add modal
  const openQuickAdd = () => {
    setShowItemNotFound(false);
    fetchQuickAddData();
    setQuickAddForm({
      sku: notFoundSku,
      name: "",
      cost_price: "",
      retail_price: "",
      gp_percent: "",
      category: "",
      sub_category: "",
      group: "",
      mfg: "",
      vendor_ids: [],
      units_per_case: "",
      tax_1: false,
      tax_2: false,
      foodstamp: false
    });
    setShowQuickAdd(true);
    // Shared cross-store item catalog — best-effort suggestion, silently no-ops if not found/unreachable
    if (notFoundSku) {
      const token = localStorage.getItem("token");
      axios.get(`${API}/products/item-catalog-lookup/${notFoundSku}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          if (res.data.found) {
            setQuickAddForm(prev => ({
              ...prev,
              name: (res.data.item_name || "").toUpperCase(),
              units_per_case: res.data.units_per_case != null ? String(res.data.units_per_case) : prev.units_per_case,
            }));
          }
        })
        .catch(() => {});
    }
  };

  // GP calculation helpers
  const calculateGpFromPrices = (cost, retail) => {
    if (!cost || !retail || parseFloat(retail) === 0) return "";
    const gp = ((parseFloat(retail) - parseFloat(cost)) / parseFloat(retail)) * 100;
    return gp.toFixed(1);
  };

  const calculateRetailFromGp = (cost, gpPercent) => {
    if (!cost || !gpPercent || parseFloat(gpPercent) >= 100) return "";
    const retail = parseFloat(cost) / (1 - parseFloat(gpPercent) / 100);
    return retail.toFixed(2);
  };

  const calculateCostFromGp = (retail, gpPercent) => {
    if (!retail || !gpPercent) return "";
    const cost = parseFloat(retail) * (1 - parseFloat(gpPercent) / 100);
    return cost.toFixed(2);
  };

  // Handle Quick Add form changes
  const handleQuickAddChange = (field, value) => {
    setQuickAddForm(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate GP/Price based on which field changed
      // 1. Cost changes → recalculate Retail from GP%, else recalculate GP%
      if (field === "cost_price") {
        if (value && updated.gp_percent) {
          updated.retail_price = calculateRetailFromGp(value, updated.gp_percent);
        } else if (updated.retail_price && value) {
          updated.gp_percent = calculateGpFromPrices(value, updated.retail_price);
        }
      // 2. Retail changes → recalculate GP%
      } else if (field === "retail_price") {
        if (updated.cost_price && value) {
          updated.gp_percent = calculateGpFromPrices(updated.cost_price, value);
        }
      // 3. GP% changes → recalculate Retail from Cost & GP%
      } else if (field === "gp_percent") {
        if (updated.cost_price && value) {
          updated.retail_price = calculateRetailFromGp(updated.cost_price, value);
        }
      }
      
      return updated;
    });
  };

  // Handle category change - auto-fill flags
  const handleQuickAddCategoryChange = (categoryId) => {
    const selectedCat = categories.find(c => c.id === categoryId);
    setQuickAddForm(prev => ({
      ...prev,
      category: categoryId,
      sub_category: "",
      tax_1: selectedCat?.tax_1 || false,
      tax_2: selectedCat?.tax_2 || false,
      foodstamp: selectedCat?.foodstamp || false,
      gp_percent: selectedCat?.gp_margin?.toString() || prev.gp_percent
    }));
  };

  // Submit Quick Add
  const handleQuickAddSubmit = async () => {
    if (!quickAddForm.sku || !quickAddForm.name || !quickAddForm.retail_price || !quickAddForm.category) {
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const productData = {
        sku: quickAddForm.sku,
        name: quickAddForm.name,
        cost_price: parseFloat(quickAddForm.cost_price) || 0,
        retail_price: parseFloat(quickAddForm.retail_price),
        category: quickAddForm.category,
        sub_category: quickAddForm.sub_category,
        group: quickAddForm.group,
        mfg: quickAddForm.mfg,
        vendor_ids: quickAddForm.vendor_ids,
        units_per_case: quickAddForm.units_per_case ? parseInt(quickAddForm.units_per_case, 10) : null,
        tax_1: quickAddForm.tax_1,
        tax_2: quickAddForm.tax_2,
        foodstamp: quickAddForm.foodstamp
      };
      
      const response = await axios.post(`${API}/products`, productData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Add to cart immediately
      const newProduct = response.data;
      addToCart({
        ...newProduct,
        price: newProduct.retail_price
      });
      
      // Refresh products and close modal
      fetchProducts();
      setShowQuickAdd(false);
      scanInputRef.current?.focus();
    } catch (error) {
      console.error("Failed to add product", error);
    }
  };

  const handleQuickSaleClick = (category) => {
    setSelectedQuickSale(category);
    setShowNumpad(true);
  };

  const handleQuickSaleSubmit = (amount) => {
    if (!selectedQuickSale || amount <= 0) return;

    const quickSaleItem = {
      product_id: `quick-sale-${selectedQuickSale.id}-${Date.now()}`,
      product_name: selectedQuickSale.name,
      price: amount,
      quantity: 1,
      subtotal: amount,
      // Store the flags for tax/deposit calculation
      tax_1: selectedQuickSale.tax_1,
      tax_2: selectedQuickSale.tax_2,
      deposit: selectedQuickSale.deposit,
      foodstamp: selectedQuickSale.foodstamp,
      category: selectedQuickSale.name,
      is_quick_sale: true
    };

    setCart([...cart, quickSaleItem]);
    // toast removed
    setSelectedQuickSale(null);
    // Re-focus scan input
    scanInputRef.current?.focus();
  };

  // Handle barcode scan
  const handleScan = async (e) => {
    // While the age dialog is open, ignore Enter on the scan input so any
    // residual AAMVA chars are not sent as a product lookup.
    if (ageCheckOpen) {
      if (e.key === 'Enter') { setScanInput(""); }
      return;
    }
    if (e.key === 'Enter' && scanInput.trim()) {
      const sku = scanInput.trim();
      try {
        const response = await axios.get(`${API}/products/lookup/${sku}`);
        if (response.data.found) {
          const product = response.data.product;
          addToCart(product);
          playSuccessSound();
        } else {
          // Item not found - play error sound and show modal
          playErrorSound();
          setNotFoundSku(sku);
          setShowItemNotFound(true);
        }
      } catch (error) {
        // Item not found - play error sound and show modal
        playErrorSound();
        setNotFoundSku(sku);
        setShowItemNotFound(true);
      }
      setScanInput("");
      scanInputRef.current?.focus();
    }
  };


  const handleLongPress = (product, position) => {
    const buttonPos = buttonPositions.find(bp => bp.position === position);
    setConfigPosition(position);
    setConfigProduct(product?.isGroup ? null : product);
    setConfigDisplayName(buttonPos?.display_name);
    setConfigCardStyle(buttonPos?.card_style || null);
    setConfigBorderColor(buttonPos?.border_color || null);
    setConfigSubButtons(buttonPos?.sub_buttons || null);
    setConfigButtonImage(buttonPos?.button_image || null);
    setConfigSubButtonsImage(buttonPos?.sub_buttons_image || null);
    setShowButtonConfig(true);
  };

  const handleBlankCardClick = (position) => {
    setConfigPosition(position);
    setConfigProduct(null);
    setConfigDisplayName(null);
    setConfigCardStyle(null);
    setConfigBorderColor(null);
    setConfigSubButtons(null);
    setConfigButtonImage(null);
    setConfigSubButtonsImage(null);
    setShowButtonConfig(true);
  };

  const getButtonLayout = () => {
    const layout = [];
    const gridSize = 28; // 7 columns x 4 rows; last row reserved for Vendors / To-Do / future buttons
    
    // Create a map of positions to button configurations
    const positionMap = {};
    buttonPositions.forEach(bp => {
      if (bp.product_id) {
        const product = products.find(p => p.id === bp.product_id);
        if (product) {
          positionMap[bp.position] = {
            product,
            displayName: bp.display_name,
            cardStyle: bp.card_style,
            borderColor: bp.border_color,
            subButtons: bp.sub_buttons || null,
            buttonImage: bp.button_image || null,
            subButtonsImage: bp.sub_buttons_image || null
          };
        }
      } else if (bp.sub_buttons && bp.sub_buttons.length > 0) {
        // Sub-button-only group (no primary product)
        positionMap[bp.position] = {
          product: { id: `group-${bp.position}`, name: bp.display_name || "Group", image_url: bp.button_image, isGroup: true },
          displayName: bp.display_name,
          cardStyle: bp.card_style,
          borderColor: bp.border_color,
          subButtons: bp.sub_buttons,
          buttonImage: bp.button_image || null,
          subButtonsImage: bp.sub_buttons_image || null
        };
      }
    });

    // Fill the grid with all 40 positions
    const filteredIds = new Set(filteredProducts.map(p => p.id));

    for (let i = 0; i < gridSize; i++) {
      if (positionMap[i]) {
        const isGroup = positionMap[i].product?.isGroup;
        // Group buttons always show; product buttons only if they match filter
        if (isGroup || filteredIds.has(positionMap[i].product.id)) {
          layout.push({
            position: i,
            product: positionMap[i].product,
            displayName: positionMap[i].displayName,
            cardStyle: positionMap[i].cardStyle,
            borderColor: positionMap[i].borderColor,
            subButtons: positionMap[i].subButtons,
            buttonImage: positionMap[i].buttonImage,
            subButtonsImage: positionMap[i].subButtonsImage,
            isBlank: false
          });
        } else {
          // Position has product but doesn't match filter - show as blank
          layout.push({
            position: i,
            product: null,
            displayName: null,
            isBlank: true
          });
        }
      } else {
        // No product at this position - show as blank
        layout.push({
          position: i,
          product: null,
          displayName: null,
          isBlank: true
        });
      }
    }

    return layout;
  };

  // --- Age Verification Helpers ---
  // Determine required age for a product (product-level flag OR its category's flag)
  const requiredAgeForProduct = (product) => {
    if (!product) return 0;
    // Direct product flags
    if (product.age_21_plus) return 21;
    if (product.age_18_plus) return 18;
    // Resolve category — product.category can be an ID or a name
    const catRef = product.category || "";
    if (!catRef) return 0;
    const cat = categories.find(c => c.id === catRef || c.name === catRef);
    if (!cat) return 0;
    if (cat.age_21_plus) return 21;
    if (cat.age_18_plus) return 18;
    return 0;
  };

  // Fluid oz limit — cumulative check across a category's items already in the cart.
  // Returns false (and shows a warning toast) if adding `additionalOz` more fluid
  // ounces of `categoryName` would push that category's transaction total over its cap.
  const checkFluidLimit = (categoryName, additionalOz) => {
    if (additionalOz <= 0) return true;
    const cat = categories.find((c) => c.name === categoryName);
    if (!cat?.fluid_limit_enabled || !cat?.max_fluid_oz) return true;
    const currentOz = cart
      .filter((item) => !item.is_refund && item.category === categoryName)
      .reduce((sum, item) => sum + (item.fluid_oz || 0) * item.quantity, 0);
    const newTotal = currentOz + additionalOz;
    if (newTotal > cat.max_fluid_oz) {
      toast.error(`Fluid limit reached: ${cat.name} is capped at ${cat.max_fluid_oz}oz per transaction`);
      return false;
    }
    return true;
  };

  const addToCart = (product) => {
    // If transaction is complete, clear (fresh session) — age flags reset too
    if (transactionComplete) {
      setCart([]);
      setTransactionComplete(false);
      setCompletedPaymentMethod(null);
      setMixMatchDiscount({ applied_discounts: [], total_savings: 0 });
      setVerifiedAge(null);
      setVerifiedCustomer(null);
      setAgeVerifications([]);
      setAgeSkipEvents([]);
    }

    // --- Age gate — refunds are never age-gated (returning a product) ---
    if (!isRefundMode) {
      const required = requiredAgeForProduct(product);
      if (required > 0) {
        // Rule 1: session already verified age >= required → add silently
        if (verifiedAge != null && verifiedAge >= required) {
          return doAddToCart(product);
        }
        // Rule 2a: no verification yet → open dialog
        // Rule 2b: session verified age < required → open dialog in "block" mode
        setAgeCheckRequired(required);
        setPendingAgeProduct(product);
        // Clear the scan input immediately — in case any AAMVA prefix bytes
        // leaked in before the dialog mounted its keydown listener.
        setScanInput("");
        setAgeCheckOpen(true);
        return;
      }
    }

    return doAddToCart(product);
  };

  const doAddToCart = (product) => {
    const basePrice = product.retail_price || product.price || 0;
    
    // Check if we need to ask for price (when ask_for_price is enabled and price is 0)
    if (product.ask_for_price && basePrice === 0) {
      setPriceInputProduct(product);
      setShowPriceInput(true);
      return;
    }

    // Fluid oz limit — hard block if adding this item would push the category's
    // cumulative fluid ounces (already-in-cart + this item) over its per-transaction cap.
    if (!isRefundMode) {
      const catId = product.category || "";
      const productCategory = categories.find(c => c.id === catId) || categories.find(c => c.name === catId);
      const productOz = parseFloat(product.fluid_oz) || 0;
      if (productCategory && !checkFluidLimit(productCategory.name, productOz)) {
        return;
      }
    }
    
    const price = isRefundMode ? -Math.abs(basePrice) : basePrice;
    const productId = isRefundMode ? `refund-${product.id}-${Date.now()}` : product.id;
    
    // Build cart item with tax and foodstamp indicators
    const cartItem = {
      product_id: productId,
      product_name: isRefundMode ? `REFUND: ${product.name}` : product.name,
      sku: product.sku || "",
      price: price,
      quantity: 1,
      subtotal: price,
      is_refund: isRefundMode,
      // Tax indicators (use both naming conventions for compatibility)
      tax_1: product.tax_1 || false,
      tax_2: product.tax_2 || false,
      // Foodstamp/EBT indicator
      foodstamp: product.foodstamp || false,
      // Deposit indicator
      deposit: product.deposit || false,
      // Fluid ounces (per unit) — used to enforce category fluid limits
      fluid_oz: parseFloat(product.fluid_oz) || 0,
      // Category info for reporting — resolve UUID to name
      category: (() => {
        const catId = product.category || "";
        if (!catId || catId.length < 20) return catId; // Already a name
        const cat = categories.find(c => c.id === catId);
        return cat?.name || catId;
      })(),
      sub_category: product.sub_category || "",
      // Group info for mix & match
      group: product.group || "",
      // Manufacturer info for buydowns
      mfg: product.mfg || "",
    };
    
    if (!isRefundMode && !transactionComplete) {
      const existingItem = cart.find((item) => item.product_id === product.id);
      if (existingItem) {
        setCart(
          cart.map((item) =>
            item.product_id === product.id
              ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.price }
              : item
          )
        );
        setTimeout(() => scanInputRef.current?.focus(), 100);
        return;
      }
    }
    
    // If transaction was complete, start fresh with just this item
    if (transactionComplete) {
      setCart([cartItem]);
    } else {
      setCart([...cart, cartItem]);
    }
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };
  
  // Add product with custom price (from Ask For Price dialog)
  const addToCartWithPrice = (customPrice) => {
    if (!priceInputProduct || customPrice <= 0) {
      setShowPriceInput(false);
      setPriceInputProduct(null);
      return;
    }
    
    const product = priceInputProduct;
    const price = isRefundMode ? -Math.abs(customPrice) : customPrice;
    const productId = isRefundMode 
      ? `refund-${product.id}-${Date.now()}` 
      : `price-entry-${product.id}-${Date.now()}`;
    
    setCart([
      ...cart,
      {
        product_id: productId,
        product_name: isRefundMode ? `REFUND: ${product.name}` : product.name,
        sku: product.sku || "",
        price: price,
        quantity: 1,
        subtotal: price,
        is_refund: isRefundMode,
        is_manual_price: true,
        // Tax indicators
        tax_1: product.tax_1 || false,
        tax_2: product.tax_2 || false,
        // Foodstamp/EBT indicator
        foodstamp: product.foodstamp || false,
        // Deposit indicator
        deposit: product.deposit || false,
        // Category info for reporting — resolve UUID to name
        category: (() => {
          const catId = product.category || "";
          if (!catId || catId.length < 20) return catId;
          const cat = categories.find(c => c.id === catId);
          return cat?.name || catId;
        })(),
        sub_category: product.sub_category || "",
      },
    ]);
    
    setShowPriceInput(false);
    setPriceInputProduct(null);
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const removeFromCart = async (productId) => {
    // Don't allow removing items if transaction is complete
    if (transactionComplete) return;
    
    const itemToRemove = cart.find((item) => item.product_id === productId);
    
    // Record the deleted item
    if (itemToRemove) {
      try {
        const token = localStorage.getItem("token");
        const product = products.find(p => p.id === productId);
        await axios.post(`${API}/deleted-items`, {
          product_id: productId,
          product_name: itemToRemove.product_name,
          sku: product?.sku || "",
          quantity: itemToRemove.quantity,
          price: itemToRemove.price,
          subtotal: itemToRemove.subtotal,
          employee_id: employee.id,
          employee_name: employee.name,
          reason: "Item removed from cart",
          station_id: station?.station_id || localStorage.getItem("station_id") || "default"
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Failed to record deleted item", error);
      }
    }
    
    setCart(cart.filter((item) => item.product_id !== productId));
    // If that was the last item, reset age-verification session memory —
    // an empty cart means the next scan could be for a different customer.
    if (cart.filter((item) => item.product_id !== productId).length === 0) {
      setVerifiedAge(null);
      setVerifiedCustomer(null);
      setAgeVerifications([]);
      setAgeSkipEvents([]);
    }
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  // Start a new transaction (clear completed state)
  const startNewTransaction = () => {
    setCart([]);
    setTransactionComplete(false);
    setCompletedPaymentMethod(null);
    setMixMatchDiscount({ applied_discounts: [], total_savings: 0 });
    setBuydownSavings(0);
    setBuydownDetails([]);
    setVerifiedAge(null);
    setVerifiedCustomer(null);
    setAgeVerifications([]);
    setAgeSkipEvents([]);
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const updateQuantity = async (productId, newQuantity) => {
    // Don't allow updating if transaction is complete
    if (transactionComplete) return;
    
    if (newQuantity <= 0) {
      await removeFromCart(productId);
      return;
    }

    const item = cart.find((i) => i.product_id === productId);
    if (item && newQuantity > item.quantity && !item.is_refund) {
      const additionalOz = (item.fluid_oz || 0) * (newQuantity - item.quantity);
      if (!checkFluidLimit(item.category, additionalOz)) return;
    }

    setCart(
      cart.map((item) =>
        item.product_id === productId
          ? { ...item, quantity: newQuantity, subtotal: newQuantity * item.price }
          : item
      )
    );
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const clearCart = () => {
    setCart([]);
    setIsRefundMode(false);
    setOriginalRefundTxn("");
    setOriginalRefundData(null);
    // Reset age verification session
    setVerifiedAge(null);
    setVerifiedCustomer(null);
    setAgeVerifications([]);
    setAgeSkipEvents([]);
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  // POS Action Functions
  const handleHold = () => {
    // If transaction just completed, clear it first before handling holds
    if (transactionComplete) {
      setCart([]);
      setTransactionComplete(false);
      setCompletedPaymentMethod(null);
      setMixMatchDiscount({ applied_discounts: [], total_savings: 0 });
      setBuydownSavings(0);
      setBuydownDetails([]);
      
      // Now handle retrieval
      if (heldTransactions.length === 1) {
        const lastHeld = heldTransactions[0];
        setCart(lastHeld.items);
        setHeldTransactions([]);
        setTimeout(() => scanInputRef.current?.focus(), 100);
      } else if (heldTransactions.length > 1) {
        setShowHoldDialog(true);
      }
      return;
    }

    if (cart.length === 0 && heldTransactions.length === 0) {
      return;
    }
    
    if (cart.length > 0) {
      // Put current transaction on hold
      const holdId = Date.now();
      setHeldTransactions([...heldTransactions, { id: holdId, items: [...cart], timestamp: new Date() }]);
      setCart([]);
      setIsRefundMode(false);
      setTimeout(() => scanInputRef.current?.focus(), 100);
    } else if (heldTransactions.length === 1) {
      // Only one transaction - retrieve directly
      const lastHeld = heldTransactions[0];
      setCart(lastHeld.items);
      setHeldTransactions([]);
      setTimeout(() => scanInputRef.current?.focus(), 100);
    } else if (heldTransactions.length > 1) {
      // Multiple transactions - show dialog to select
      setShowHoldDialog(true);
    }
  };

  const retrieveHeldTransaction = (holdId) => {
    const transaction = heldTransactions.find(t => t.id === holdId);
    if (transaction) {
      // Clear any completed transaction state first
      setTransactionComplete(false);
      setCompletedPaymentMethod(null);
      setMixMatchDiscount({ applied_discounts: [], total_savings: 0 });
      setBuydownSavings(0);
      setBuydownDetails([]);
      
      setCart(transaction.items);
      setHeldTransactions(heldTransactions.filter(t => t.id !== holdId));
      setShowHoldDialog(false);
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  const deleteHeldTransaction = async (holdId) => {
    const transaction = heldTransactions.find(t => t.id === holdId);
    
    // Record the deleted held transaction
    if (transaction) {
      try {
        const token = localStorage.getItem("token");
        await axios.post(`${API}/voids`, {
          void_type: "hold_deleted",
          employee_id: employee.id,
          employee_name: employee.name,
          items: transaction.items.map(item => {
            const product = products.find(p => p.id === item.product_id);
            return {
              product_id: item.product_id,
              product_name: item.product_name,
              sku: product?.sku || "",
              quantity: item.quantity,
              price: item.price,
              subtotal: item.subtotal,
            };
          }),
          subtotal: transaction.items.reduce((sum, item) => sum + item.subtotal, 0),
          reason: "Held transaction deleted from retrieve dialog",
          station_id: station?.station_id || localStorage.getItem("station_id") || "default"
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Failed to record deleted held transaction", error);
      }
    }
    
    setHeldTransactions(heldTransactions.filter(t => t.id !== holdId));
    // toast removed
    if (heldTransactions.length <= 1) {
      setShowHoldDialog(false);
    }
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const formatHoldTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getHoldTotal = (items) => {
    return items.reduce((sum, item) => sum + item.subtotal, 0);
  };

  const handleVoid = async () => {
    if (cart.length === 0) {
      // toast removed
      return;
    }
    
    // Record the void
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/voids`, {
        void_type: "full_void",
        employee_id: employee.id,
        employee_name: employee.name,
        items: cart.map(item => {
          const product = products.find(p => p.id === item.product_id);
          return {
            product_id: item.product_id,
            product_name: item.product_name,
            sku: product?.sku || "",
            quantity: item.quantity,
            price: item.price,
            subtotal: item.subtotal,
          };
        }),
        subtotal: cart.reduce((sum, item) => sum + item.subtotal, 0),
        reason: "Void button pressed",
        station_id: station?.station_id || localStorage.getItem("station_id") || "default"
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Failed to record void", error);
    }
    
    setCart([]);
    setIsRefundMode(false);
    // Reset age verification session
    setVerifiedAge(null);
    setVerifiedCustomer(null);
    setAgeVerifications([]);
    setAgeSkipEvents([]);
    // toast removed
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleRefund = () => {
    setIsRefundMode(!isRefundMode);
    if (!isRefundMode) {
      setOriginalRefundTxn("");
      setOriginalRefundData(null);
    }
    if (!isRefundMode) {
      // toast removed
    } else {
      // toast removed
    }
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleNoSale = async () => {
    // Kick the drawer FIRST (instant), record the no-sale event in parallel.
    const token = localStorage.getItem("token");
    const sid = station?.station_id || localStorage.getItem("station_id") || "default";
    const drawerPromise = hardwareSettings?.cash_drawer_enabled ? openCashDrawer() : null;
    let recorded = true;
    try { await axios.post(`${API}/no-sale`, { station_id: sid }, { headers: { Authorization: `Bearer ${token}` } }); } catch (e) { console.error("Failed to record no-sale", e); recorded = false; }
    if (drawerPromise) {
      const success = await drawerPromise;
      if (success) {
        toast.success(recorded ? "No Sale recorded - cash drawer opened" : "Cash drawer opened (but no-sale event failed to save)");
      } else {
        toast.error(recorded ? "No Sale recorded - but cash drawer failed to open" : "No Sale failed - cash drawer also failed");
      }
    } else {
      if (recorded) toast.success("No Sale recorded");
      else toast.error("Failed to record No Sale");
    }
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handlePrintLastReceipt = async () => {
    const result = await printLastReceipt();
    if (result.success) {
      toast.success(`Reprinted: ${result.transactionNumber}`);
    } else {
      toast.error(result.error || "Failed to print last receipt");
    }
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  // Price Change Functions
  const handleCartItemClick = (productId) => {
    if (selectedCartItemId === productId) {
      setSelectedCartItemId(null); // Deselect if clicking same item
    } else {
      setSelectedCartItemId(productId);
    }
  };

  const openPriceChangeDialog = () => {
    if (!selectedCartItemId) {
      toast.error("Please select an item first");
      return;
    }
    const item = cart.find(i => i.product_id === selectedCartItemId);
    if (item) {
      setNewPriceInput(""); // Start with blank field
      setShowPriceChangeDialog(true);
    }
  };

  const handleDiscountItem = () => {
    const newPrice = parseFloat(newPriceInput);
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error("Please enter a valid price");
      return;
    }
    
    // Update cart item with new price (discount for this transaction only)
    setCart(cart.map(item => {
      if (item.product_id === selectedCartItemId) {
        return {
          ...item,
          originalPrice: item.originalPrice || item.price, // Keep original if not already set
          price: newPrice,
          subtotal: newPrice * item.quantity,
          priceEdited: true
        };
      }
      return item;
    }));
    
    setShowPriceChangeDialog(false);
    setSelectedCartItemId(null);
    setNewPriceInput("");
    toast.success("Price discounted for this transaction");
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handlePermanentPriceChange = async () => {
    const newPrice = parseFloat(newPriceInput);
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error("Please enter a valid price");
      return;
    }
    
    const item = cart.find(i => i.product_id === selectedCartItemId);
    if (!item) return;
    
    try {
      const token = localStorage.getItem("token");
      // Get current product data first
      const productRes = await axios.get(`${API}/products/${selectedCartItemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const currentProduct = productRes.data;
      
      // Update price in database using PUT with full product data
      await axios.put(`${API}/products/${selectedCartItemId}`, {
        ...currentProduct,
        price: newPrice,
        retail_price: newPrice
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update cart item
      setCart(cart.map(cartItem => {
        if (cartItem.product_id === selectedCartItemId) {
          return {
            ...cartItem,
            originalPrice: cartItem.originalPrice || cartItem.price,
            price: newPrice,
            subtotal: newPrice * cartItem.quantity,
            priceEdited: true
          };
        }
        return cartItem;
      }));
      
      // Update products list
      setProducts(products.map(p => {
        if (p.id === selectedCartItemId) {
          return { ...p, price: newPrice };
        }
        return p;
      }));
      
      setShowPriceChangeDialog(false);
      setSelectedCartItemId(null);
      setNewPriceInput("");
      toast.success("Price changed permanently");
    } catch (error) {
      console.error("Failed to update price", error);
      toast.error("Failed to update price");
    }
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
  
  // Calculate tax based on product's tax_1 and tax_2 flags (tax applied AFTER discounts)
  const calculateTaxAndDeposit = () => {
    let totalTax = 0;
    let totalDeposit = 0;
    const tax1Rate = (storeSettings?.tax_1_rate || 0) / 100;
    const tax2Rate = (storeSettings?.tax_2_rate || 0) / 100;
    const depositAmount = storeSettings?.deposit_amount || 0;

    cart.forEach(item => {
      // Calculate this item's discount (Mix n Match + Buydown)
      let itemDiscount = 0;
      const mmInfo = mixMatchDiscount.applied_discounts?.find(d =>
        d.applied_products?.includes(item.product_id)
      );
      if (mmInfo) {
        const itemShare = item.subtotal / (mmInfo.original_total || 1);
        itemDiscount += (mmInfo.savings || 0) * itemShare;
      }
      const bdInfo = buydownDetails.find(b => b.product_id === item.product_id);
      if (bdInfo) itemDiscount += bdInfo.savings || 0;

      const taxableAmount = Math.max(0, item.subtotal - itemDiscount);

      // Use cart item's own tax/deposit flags (set when added to cart)
      if (item.tax_1) totalTax += taxableAmount * tax1Rate;
      if (item.tax_2) totalTax += taxableAmount * tax2Rate;
      if (item.deposit) totalDeposit += depositAmount * item.quantity;
    });

    return { tax: totalTax, deposit: totalDeposit };
  };

  const { tax, deposit } = calculateTaxAndDeposit();
  const mixMatchSavings = mixMatchDiscount.total_savings || 0;
  const total = subtotal + tax + deposit - mixMatchSavings - buydownSavings;

  // Calculate FS-eligible total (foodstamp items after discounts, NO tax for EBT Food)
  const fsEligibleTotal = (() => {
    let fsNet = 0;

    cart.forEach(item => {
      if (!item.foodstamp) return;

      // Calculate this item's discount
      let itemDiscount = 0;
      const mmInfo = mixMatchDiscount.applied_discounts?.find(d =>
        d.applied_products?.includes(item.product_id)
      );
      if (mmInfo) itemDiscount += (mmInfo.savings || 0) * (item.subtotal / (mmInfo.original_total || 1));
      const bdInfo = buydownDetails.find(b => b.product_id === item.product_id);
      if (bdInfo) itemDiscount += bdInfo.savings || 0;

      const itemNet = Math.max(0, item.subtotal - itemDiscount);
      fsNet += itemNet;
    });

    return Math.round(fsNet * 100) / 100;
  })();

  // Calculate tax on FS-eligible items (removed from total when EBT Food is used)
  const fsTax = (() => {
    let fsTaxAmount = 0;
    const tax1Rate = (storeSettings?.tax_1_rate || 0) / 100;
    const tax2Rate = (storeSettings?.tax_2_rate || 0) / 100;

    cart.forEach(item => {
      if (!item.foodstamp) return;

      let itemDiscount = 0;
      const mmInfo = mixMatchDiscount.applied_discounts?.find(d =>
        d.applied_products?.includes(item.product_id)
      );
      if (mmInfo) itemDiscount += (mmInfo.savings || 0) * (item.subtotal / (mmInfo.original_total || 1));
      const bdInfo = buydownDetails.find(b => b.product_id === item.product_id);
      if (bdInfo) itemDiscount += bdInfo.savings || 0;

      const taxableAmount = Math.max(0, item.subtotal - itemDiscount);
      if (item.tax_1) fsTaxAmount += taxableAmount * tax1Rate;
      if (item.tax_2) fsTaxAmount += taxableAmount * tax2Rate;
    });

    return Math.round(fsTaxAmount * 100) / 100;
  })();

  // Calculate subtotal of FS items that have tax flags (for EOD "FS Tax Exempt Sale")
  const fsTaxableSales = (() => {
    let total = 0;
    cart.forEach(item => {
      const product = item.is_quick_sale ? item : products.find(p => p.id === item.product_id);
      if (!product?.foodstamp) return;
      if (!product.tax_1 && !product.tax_2) return;

      let itemDiscount = 0;
      const mmInfo = mixMatchDiscount.applied_discounts?.find(d =>
        d.applied_products?.includes(item.product_id)
      );
      if (mmInfo) itemDiscount += (mmInfo.savings || 0) * (item.subtotal / (mmInfo.original_total || 1));
      const bdInfo = buydownDetails.find(b => b.product_id === item.product_id);
      if (bdInfo) itemDiscount += bdInfo.savings || 0;

      total += Math.max(0, item.subtotal - itemDiscount);
    });
    return Math.round(total * 100) / 100;
  })();

  // Surcharge computed values for quick pay buttons
  const surchargeAmount = paxSettings?.cc_db_surcharge || 0;
  const minCreditDebitAmount = paxSettings?.min_credit_debit_amount || 0;
  const hasSurchargeInCart = cart.some(item => item.sku === "CC-DB-SURCHARGE");

  // Calculate buydown savings whenever cart changes
  useEffect(() => {
    const calculateBuydowns = async () => {
      if (cart.length === 0) {
        setBuydownSavings(0);
        setBuydownDetails([]);
        return;
      }

      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };
        
        let totalBuydownSavings = 0;
        const details = [];
        // Units already discounted per buydown — honours "limit per transaction"
        const unitsUsed = {};

        for (const item of cart) {
          if (item.is_refund) continue;
          
          // Find the product to get its group
          const product = products.find(p => p.id === item.product_id);
          const group = product?.group || "";
          
          const response = await axios.post(`${API}/buydowns/calculate`, {
            sku: item.sku || "",
            group: group
          }, { headers });
          
          if (response.data.has_buydown) {
            const cap = response.data.max_units_per_transaction;
            const buydownId = response.data.buydown_id;
            let discountedQty = item.quantity;
            if (cap) {
              const used = unitsUsed[buydownId] || 0;
              discountedQty = Math.max(0, Math.min(discountedQty, cap - used));
              if (discountedQty === 0) continue;
              unitsUsed[buydownId] = used + discountedQty;
            }
            const savings = response.data.buydown_amount * discountedQty;
            totalBuydownSavings += savings;
            details.push({
              product_id: item.product_id,  // Include product_id for matching during enrichment
              product_name: item.product_name,
              buydown_name: response.data.buydown_name,
              buydown_amount: response.data.buydown_amount,
              funding: response.data.funding || "retailer",
              offer_name: response.data.offer_name || "",
              quantity: discountedQty,
              savings: savings
            });
          }
        }
        
        setBuydownSavings(totalBuydownSavings);
        setBuydownDetails(details);
      } catch (error) {
        console.error("Failed to calculate buydowns", error);
      }
    };

    calculateBuydowns();
  }, [cart, products]);

  // Sync cart data to Customer Display via localStorage — only when the
  // Hardware Settings toggle is actually on, so a second-screen tab left
  // open doesn't keep showing live sale data while it's supposed to be off.
  useEffect(() => {
    if (!hardwareSettings?.customer_display_enabled) {
      localStorage.removeItem("customer_display_data");
      return;
    }
    const displayData = {
      cart: cart.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        price: item.price,
        quantity: item.quantity,
        subtotal: item.subtotal,
        is_refund: item.is_refund || false,
      })),
      totals: {
        subtotal,
        tax,
        deposit,
        mixMatchSavings,
        buydownSavings,
        total,
      },
      storeName: storeSettings?.store_name || "C-Store POS",
      adImages: hardwareSettings?.customer_display_ad_images || [],
      transactionComplete: transactionComplete || !!pendingChange,
      paymentInfo: transactionComplete ? {
        method: completedPaymentMethod,
        amountTendered: completedAmountTendered,
        changeDue: completedChangeDue,
      } : pendingChange ? { method: "cash", amountTendered: pendingChange.amountTendered, changeDue: pendingChange.changeDue } : null,
    };
    localStorage.setItem("customer_display_data", JSON.stringify(displayData));
  }, [cart, subtotal, tax, deposit, mixMatchSavings, buydownSavings, total, storeSettings, hardwareSettings, transactionComplete, completedPaymentMethod, completedAmountTendered, completedChangeDue, pendingChange]);

  // Live cart display on the ModeskP1 pad while ringing up — only when Modesk Payment is the
  // active terminal for this register. Debounced so fast scanning doesn't flood the pad, and
  // best-effort: errors are swallowed since this is a nicety, never allowed to slow the register.
  const cartPushTimer = useRef(null);
  const pushCartToPad = useCallback((items, amounts) => {
    if (paxSettings?.driver !== "dejavoo") return;
    if (paxSettings?.cart_display_enabled === false) return;
    const stationId = localStorage.getItem("station_id") || "default";
    axios.post(`${API}/dejavoo/cart?station_id=${stationId}`, { items, amounts },
      { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }).catch(() => {});
  }, [paxSettings]);
  useEffect(() => {
    if (paxSettings?.driver !== "dejavoo") return;
    if (paxSettings?.cart_display_enabled === false) return;
    if (cartPushTimer.current) clearTimeout(cartPushTimer.current);
    // Stop scheduling cosmetic cart pushes once the Payment Dialog is open (the terminal is
    // about to / already handling the real Sale request — overlapping with that makes the pad
    // reject the Sale as "Terminal in use"; the backend also serializes these via a shared lock
    // as a second line of defense, but not even scheduling a push here avoids that wait too) OR
    // once the sale is complete (the explicit clear in onSuccess below is the only thing allowed
    // to touch the pad at that point — `cart` itself isn't reset until the NEXT sale's first item
    // is added, so re-running this with the stale just-sold cart would undo that clear).
    if (showPaymentDialog || transactionComplete) return;
    cartPushTimer.current = setTimeout(() => {
      const items = cart.map((item) => ({ name: item.product_name, price: item.price, quantity: item.quantity }));
      const r2 = (v) => Math.round(v * 100) / 100;
      const amounts = [{ name: "Subtotal", value: r2(subtotal) }];
      if (tax) amounts.push({ name: "Tax", value: r2(tax) });
      if (deposit) amounts.push({ name: "Deposit", value: r2(deposit) });
      if (mixMatchSavings) amounts.push({ name: "Mix & Match Savings", value: -r2(mixMatchSavings) });
      if (buydownSavings) amounts.push({ name: "Savings", value: -r2(buydownSavings) });
      amounts.push({ name: "Total", value: r2(total), total: true });
      pushCartToPad(items, amounts);
    }, 500);
    return () => { if (cartPushTimer.current) clearTimeout(cartPushTimer.current); };
  }, [cart, subtotal, tax, deposit, mixMatchSavings, buydownSavings, total, paxSettings, transactionComplete, showPaymentDialog, pushCartToPad]);

  // A pending change preview only lives while the payment dialog is open
  useEffect(() => { if (!showPaymentDialog) setPendingChange(null); }, [showPaymentDialog]);

  // Filter products by search query only (no category filtering)
  // Starts-with matches come first, then contains matches
  const getFilteredProducts = () => {
    let filtered = products;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const startsWith = [];
      const contains = [];
      
      for (const product of filtered) {
        const nameMatch = product.name.toLowerCase().startsWith(q);
        const skuMatch = product.sku.toLowerCase().startsWith(q);
        if (nameMatch || skuMatch) {
          startsWith.push(product);
        } else if (
          product.name.toLowerCase().includes(q) ||
          product.category.toLowerCase().includes(q) ||
          product.sku.toLowerCase().includes(q)
        ) {
          contains.push(product);
        }
      }
      
      filtered = [...startsWith, ...contains];
    }

    return filtered;
  };

  const filteredProducts = getFilteredProducts();

  // Get active tab color
  const getActiveTabColor = () => {
    if (activeTab && tabs.length > 0) {
      const currentTab = tabs.find(tab => tab.id === activeTab);
      return currentTab?.color || "#2563EB";
    }
    return "#2563EB";
  };

  const handleLookupOriginalTxn = async () => {
    const txnNum = originalRefundTxn.trim();
    if (!txnNum) return;
    const token = localStorage.getItem("token");
    try {
      const res = await axios.get(
        `${API}/transactions/lookup?txn_number=${encodeURIComponent(txnNum)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const tdata = res.data;
      const authCodes = (tdata.tenders || [])
        .filter(t => t.authCode)
        .map(t => ({ authCode: t.authCode, card_type: t.card_type || "", last4: t.last4 || "" }));
      setOriginalRefundData({ transaction_number: tdata.transaction_number, auth_codes: authCodes });
    } catch {
      toast.error("Original transaction not found");
      setOriginalRefundData(null);
    }
  };

  const activeTabColor = getActiveTabColor();

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <Navbar employee={employee} onLogout={handleLogoutClick} displaySettings={displaySettings} station={station} isFullscreen={isFullscreen} onToggleFullscreen={toggleFullscreen} />
      
      {/* Refund Mode Indicator */}
      {isRefundMode && (
        <div className="bg-red-500 text-white py-1.5 px-4 flex items-center justify-between gap-4">
          <span className="font-bold text-base">REFUND MODE — Items added as negative</span>
          <div className="flex items-center gap-2 text-sm shrink-0">
            <span className="text-red-100 text-xs">Original TXN#:</span>
            <input
              className="text-black px-2 py-0.5 rounded text-xs w-44"
              placeholder="TXN-YYYYMMDD-..."
              value={originalRefundTxn}
              onChange={e => setOriginalRefundTxn(e.target.value.toUpperCase())}
              onBlur={handleLookupOriginalTxn}
              onKeyDown={e => e.key === "Enter" && handleLookupOriginalTxn()}
              data-testid="original-refund-txn-input"
            />
            {originalRefundData && (
              <span className="text-green-200 text-xs font-mono">
                Auth: {originalRefundData.auth_codes.map(a => a.authCode).join(", ") || "Cash"}
              </span>
            )}
          </div>
        </div>
      )}
      
      {/* Hold Indicator */}
      {heldTransactions.length > 0 && (
        <div className="bg-amber-500 text-white text-center py-1 text-sm font-medium">
          {heldTransactions.length} transaction(s) on hold - Click &quot;Hold&quot; to retrieve
        </div>
      )}
      
      <div className="flex flex-1 overflow-hidden">
        {/* Cart Sidebar - Conditionally on Left */}
        {displaySettings?.cart_position === "left" && (
          <CartPanel
            cart={cart}
            scanInput={scanInput}
            setScanInput={setScanInput}
            scanInputRef={scanInputRef}
            handleScan={handleScan}
            cartEndRef={cartEndRef}
            updateQuantity={updateQuantity}
            removeFromCart={removeFromCart}
            handleCartItemClick={handleCartItemClick}
            selectedCartItemId={selectedCartItemId}
            displaySettings={displaySettings}
            transactionComplete={transactionComplete}
            completedPaymentMethod={completedPaymentMethod}
            completedAmountTendered={completedAmountTendered}
            completedChangeDue={completedChangeDue}
            mixMatchDiscount={mixMatchDiscount}
            buydownDetails={buydownDetails}
            mixMatchSavings={mixMatchSavings}
            buydownSavings={buydownSavings}
            subtotal={subtotal}
            tax={tax}
            deposit={deposit}
            total={total}
            setShowPaymentDialog={setShowPaymentDialog}
            onQuickPay={(type) => { setQuickPayType(type); setShowPaymentDialog(true); }}
            fsEligibleTotal={fsEligibleTotal}
            surchargeAmount={surchargeAmount}
            minAmount={minCreditDebitAmount}
            hasSurchargeInCart={hasSurchargeInCart}
            onAddSurcharge={(amount) => {
              const surchargeItem = {
                product_id: `cc-db-surcharge-${Date.now()}`,
                sku: "CC-DB-SURCHARGE",
                product_name: "CC/DB Surcharge",
                price: amount, retail_price: amount, quantity: 1, subtotal: amount,
                tax_1: false, tax_2: false, foodstamp: false, deposit: false,
                category: "Surcharge", is_surcharge: true,
              };
              setCart(prev => prev.some(item => item.sku === "CC-DB-SURCHARGE") ? prev : [...prev, surchargeItem]);
            }}
          />
        )}

        {/* Product Grid */}
        <ProductGrid
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          activeTabColor={activeTabColor}
          loading={loading}
          displaySettings={displaySettings}
          getButtonLayout={getButtonLayout}
          addToCart={addToCart}
          handleBlankCardClick={handleBlankCardClick}
          handleLongPress={handleLongPress}
          onSubButtonClick={(data) => setSubButtonPopup(data)}
          heldTransactions={heldTransactions}
          isRefundMode={isRefundMode}
          isPrinting={isPrinting}
          isOpeningDrawer={isOpeningDrawer}
          handleHold={handleHold}
          handleVoid={handleVoid}
          handleRefund={handleRefund}
          handleNoSale={handleNoSale}
          handlePrintLastReceipt={handlePrintLastReceipt}
          setShowVendorPayoutDialog={setShowVendorPayoutDialog}
          selectedCartItemId={selectedCartItemId}
          openPriceChangeDialog={openPriceChangeDialog}
          onLookup={() => setShowLookup(true)}
          onPriceCheck={() => setShowPriceCheck(true)}
          selectedCustomer={selectedCustomer}
          onAddCustomer={() => setCustomerDlg("add")}
          onSearchCustomer={() => {
            if (selectedCustomer) {
              if (window.confirm(`Remove ${selectedCustomer.full_name} from this sale?`)) setSelectedCustomer(null);
            } else {
              setCustomerDlg("search");
            }
          }}
          onAgeCheck={() => setCustomerDlg("age")}
        />

        {/* Cart Sidebar - Conditionally on Right */}
        {displaySettings?.cart_position === "right" && (
          <CartPanel
            cart={cart}
            scanInput={scanInput}
            setScanInput={setScanInput}
            scanInputRef={scanInputRef}
            handleScan={handleScan}
            cartEndRef={cartEndRef}
            updateQuantity={updateQuantity}
            removeFromCart={removeFromCart}
            handleCartItemClick={handleCartItemClick}
            selectedCartItemId={selectedCartItemId}
            displaySettings={displaySettings}
            transactionComplete={transactionComplete}
            completedPaymentMethod={completedPaymentMethod}
            completedAmountTendered={completedAmountTendered}
            completedChangeDue={completedChangeDue}
            mixMatchDiscount={mixMatchDiscount}
            buydownDetails={buydownDetails}
            mixMatchSavings={mixMatchSavings}
            buydownSavings={buydownSavings}
            subtotal={subtotal}
            tax={tax}
            deposit={deposit}
            total={total}
            setShowPaymentDialog={setShowPaymentDialog}
            onQuickPay={(type) => { setQuickPayType(type); setShowPaymentDialog(true); }}
            fsEligibleTotal={fsEligibleTotal}
            surchargeAmount={surchargeAmount}
            minAmount={minCreditDebitAmount}
            hasSurchargeInCart={hasSurchargeInCart}
            onAddSurcharge={(amount) => {
              const surchargeItem = {
                product_id: `cc-db-surcharge-${Date.now()}`,
                sku: "CC-DB-SURCHARGE",
                product_name: "CC/DB Surcharge",
                price: amount, retail_price: amount, quantity: 1, subtotal: amount,
                tax_1: false, tax_2: false, foodstamp: false, deposit: false,
                category: "Surcharge", is_surcharge: true,
              };
              setCart(prev => prev.some(item => item.sku === "CC-DB-SURCHARGE") ? prev : [...prev, surchargeItem]);
            }}
          />
        )}
      </div>

      {/* Payment Dialog */}
      <CustomerDialog open={customerDlg === "add"} onClose={() => setCustomerDlg(null)} stationId={station?.station_id} allowCreateOnly onSaved={(c) => setSelectedCustomer(c)} />
      <CustomerSearchDialog open={customerDlg === "search"} onClose={() => setCustomerDlg(null)} stationId={station?.station_id} onSelect={(c) => setSelectedCustomer(c)} />
      <CustomerAgeCheckDialog open={customerDlg === "age"} onClose={() => setCustomerDlg(null)} />
      <PaymentDialog
        open={showPaymentDialog}
        onClose={() => {
          setShowPaymentDialog(false);
          setTimeout(() => scanInputRef.current?.focus(), 100);
        }}
        cart={cart.map(item => {
          // Enrich cart items with buydown info
          const buydownInfo = buydownDetails.find(b => b.product_id === item.product_id);
          // Check if this item is part of a mix & match deal
          const mixMatchInfo = mixMatchDiscount.applied_discounts?.find(d => 
            d.applied_products?.includes(item.product_id)
          );
          return {
            ...item,
            buydown: buydownInfo ? {
              buydown_name: buydownInfo.buydown_name,
              buydown_amount: buydownInfo.buydown_amount,
              funding: buydownInfo.funding || "retailer",
              offer_name: buydownInfo.offer_name || ""
            } : null,
            mix_match_deal: mixMatchInfo ? {
              promotion_name: mixMatchInfo.promotion_name,
              savings: mixMatchInfo.savings || 0,
              funding: mixMatchInfo.funding || "retailer",
              offer_name: mixMatchInfo.offer_name || ""
            } : null,
            discount: (buydownInfo?.buydown_amount || 0) * item.quantity + (mixMatchInfo?.savings || 0)
          };
        })}
        subtotal={subtotal}
        tax={tax}
        deposit={deposit}
        total={total}
        employee={employee}
        displaySettings={displaySettings}
        isRefundMode={isRefundMode}
        originalRefundData={originalRefundData}
        mixMatchSavings={mixMatchSavings}
        buydownSavings={buydownSavings}
        fsEligibleTotal={fsEligibleTotal}
        fsTax={fsTax}
        fsTaxableSales={fsTaxableSales}
        openCashDrawer={openCashDrawer}
        quickPayType={quickPayType}
        onClearQuickPay={() => setQuickPayType(null)}
        onAddSurcharge={(amount) => {
          // Add CC/DB Surcharge line item to cart
          const surchargeItem = {
            product_id: `cc-db-surcharge-${Date.now()}`,
            sku: "CC-DB-SURCHARGE",
            product_name: "CC/DB Surcharge",
            price: amount,
            retail_price: amount,
            quantity: 1,
            subtotal: amount,
            tax_1: false,
            tax_2: false,
            foodstamp: false,
            deposit: false,
            category: "Surcharge",
            is_surcharge: true,
          };
          setCart(prev => {
            // Don't add if already exists
            if (prev.some(item => item.sku === "CC-DB-SURCHARGE")) return prev;
            return [...prev, surchargeItem];
          });
        }}
        onRemoveSurcharge={() => {
          setCart(prev => prev.filter(item => item.sku !== "CC-DB-SURCHARGE"));
        }}
        ageVerifications={ageVerifications}
        ageSkipEvents={ageSkipEvents}
        verifiedCustomer={verifiedCustomer}
        selectedCustomer={selectedCustomer}
        storeSettings={storeSettings}
        onChangePreview={setPendingChange}
        onSuccess={async (paymentMethod, amountTendered, changeDue, transactionNumber, tenders) => {
          setShowPaymentDialog(false);
          setTransactionComplete(true);
          setCompletedPaymentMethod(paymentMethod);
          setCompletedAmountTendered(amountTendered || 0);
          setCompletedChangeDue(changeDue || 0);
          // Clear the pad's cart display the instant the sale finishes — for ANY payment method,
          // not just cash. The POS's own `cart` array isn't reset until the cashier starts the
          // NEXT sale, so without this the pad would otherwise keep showing the just-completed
          // sale's items indefinitely (card payments may happen to wipe it as a side effect of
          // the terminal's own Sale-processing screen transition, but cash never touches the
          // terminal at all, so nothing else would ever clear it).
          if (cartPushTimer.current) clearTimeout(cartPushTimer.current);
          pushCartToPad([], []);
          // Clear refund linkage after successful save
          if (isRefundMode) {
            setOriginalRefundTxn("");
            setOriginalRefundData(null);
          }
          
          // --- Detect surcharge from stale closure ---
          let printCart = [...cart];
          let printTotal = total;
          let printSubtotal = subtotal;
          const tenderSum = tenders ? tenders.reduce((s, t) => s + t.amount, 0) : (amountTendered || 0);
          const surchargeGap = Math.round((tenderSum - (changeDue || 0) - total) * 100) / 100;
          const surchargeInCart = cart.some(item => item.sku === "CC-DB-SURCHARGE");
          
          if (!surchargeInCart && surchargeGap >= 0.01) {
            printCart = [...cart, {
              product_id: `cc-db-surcharge`, sku: "CC-DB-SURCHARGE",
              product_name: "CC/DB Surcharge", price: surchargeGap,
              quantity: 1, subtotal: surchargeGap, is_surcharge: true,
            }];
            printTotal = Math.round((total + surchargeGap) * 100) / 100;
            printSubtotal = Math.round((subtotal + surchargeGap) * 100) / 100;
          }
          
          // Determine if any age-restricted item was in this transaction
          const cartHadAgeItem = printCart.some(item => {
            // Check product's category (already resolved to name) or product-level flag
            const cat = categories.find(c => c.name === item.category || c.id === item.category);
            return (cat?.age_18_plus || cat?.age_21_plus) || item.age_18_plus || item.age_21_plus;
          });
          // Only print age-verified block if an age-restricted item was purchased AND we have verification
          const ageVerifiedForReceipt = (cartHadAgeItem && verifiedAge != null && ageVerifications.length > 0)
            ? {
                age: verifiedAge,
                method: verifiedCustomer?.method || "manual",
                name: verifiedCustomer?.name || null,
              }
            : null;

          // Trigger printing and cash drawer based on settings
          const totalsForPrint = {
            subtotal: printSubtotal,
            tax,
            deposit,
            discount: mixMatchSavings + buydownSavings,
            total: printTotal,
            amount_tendered: amountTendered || 0,
            change_due: changeDue || 0,
            transaction_number: transactionNumber,
            employee_name: employee?.name || 'N/A',
            station_name: localStorage.getItem('station_name') || '',
            age_verified: ageVerifiedForReceipt,
          };
          await handlePrint(storeSettings, printCart, totalsForPrint, paymentMethod, transactionNumber, tenders);

          // Loyalty: credit the attached customer (points / visit / spend) — never blocks the sale
          if (selectedCustomer?.id) {
            try {
              const r = await axios.post(`${API}/customers/${selectedCustomer.id}/visit`, { amount: printTotal, transaction_id: transactionNumber }, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
              toast.success(`${selectedCustomer.full_name}: +${r.data.earned} pts (${r.data.customer?.points} total)`);
            } catch (e) { toast.error("Sale saved, but loyalty points were not recorded"); }
            setSelectedCustomer(null);
          }

          // Reset age session after transaction is complete
          setVerifiedAge(null);
          setVerifiedCustomer(null);
          setAgeVerifications([]);
          setAgeSkipEvents([]);

          setTimeout(() => scanInputRef.current?.focus(), 100);
        }}
      />

      {/* Age Verification Dialog */}
      <AgeCheckDialog
        open={ageCheckOpen}
        requiredAge={ageCheckRequired}
        verifiedAge={verifiedAge}
        verifiedName={verifiedCustomer?.name}
        onPass={(info) => {
          // Update session verified age (keep highest)
          const newAge = Math.max(verifiedAge ?? 0, info.age || 0);
          setVerifiedAge(newAge);
          const cust = {
            name: info.name,
            dob: info.dob,
            method: info.method,
            state: info.state,
            license_no_last4: info.license_no_last4 || null,
          };
          setVerifiedCustomer(cust);
          // Resolve category name for the audit trail
          const catRef = pendingAgeProduct?.category || "";
          const catObj = catRef ? categories.find(c => c.id === catRef || c.name === catRef) : null;
          const catName = catObj?.name || (catRef.length && catRef.length < 20 ? catRef : "");
          // Audit entry
          setAgeVerifications((prev) => [
            ...prev,
            {
              required_age: ageCheckRequired,
              customer_age: info.age,
              customer_name: info.name,
              customer_dob: info.dob,
              method: info.method,
              state: info.state || null,
              license_no_last4: info.license_no_last4 || null,
              trigger_item: pendingAgeProduct?.name || pendingAgeProduct?.sku || "",
              trigger_category: catName,
              at: new Date().toISOString(),
            },
          ]);
          // Add the pending product
          if (pendingAgeProduct) {
            doAddToCart(pendingAgeProduct);
          }
          setPendingAgeProduct(null);
        }}
        onFail={() => {
          // Item not added — nothing else to do
          setPendingAgeProduct(null);
        }}
        onSkip={() => {
          // Resolve category name for the audit trail
          const catRef = pendingAgeProduct?.category || "";
          const catObj = catRef ? categories.find(c => c.id === catRef || c.name === catRef) : null;
          const catName = catObj?.name || (catRef.length && catRef.length < 20 ? catRef : "");
          // Log skip; do NOT set session verified age; add the item silently
          setAgeSkipEvents((prev) => [
            ...prev,
            {
              required_age: ageCheckRequired,
              trigger_item: pendingAgeProduct?.name || pendingAgeProduct?.sku || "",
              trigger_category: catName,
              reason: "cashier_skip",
              at: new Date().toISOString(),
            },
          ]);
          if (pendingAgeProduct) {
            doAddToCart(pendingAgeProduct);
          }
          setPendingAgeProduct(null);
        }}
        onClose={() => {
          setAgeCheckOpen(false);
          // Belt-and-suspenders: clear any residual chars that may have
          // reached the scan input despite our capture-phase preventDefault.
          setScanInput("");
          setTimeout(() => scanInputRef.current?.focus(), 150);
        }}
      />

      {/* Button Configuration Dialog */}
      <ButtonConfigDialog
        open={showButtonConfig}
        onClose={() => {
          setShowButtonConfig(false);
          setTimeout(() => scanInputRef.current?.focus(), 100);
        }}
        position={configPosition}
        currentProduct={configProduct}
        currentDisplayName={configDisplayName}
        currentCardStyle={configCardStyle}
        currentBorderColor={configBorderColor}
        currentSubButtons={configSubButtons}
        currentButtonImage={configButtonImage}
        currentSubButtonsImage={configSubButtonsImage}
        tabId={activeTab}
        allProducts={products}
        categories={categories}
        subCategories={subCategories}
        groups={groups}
        buttonPositions={buttonPositions}
        employee={employee}
        onSave={() => {
          fetchButtonPositions();
          setTimeout(() => scanInputRef.current?.focus(), 100);
        }}
        onEditProduct={(product) => {
          // Navigate to Products page with product ID to edit
          window.open(`/products?edit=${product.id}`, '_blank');
        }}
      />

      {/* Quick Sale Numpad Modal */}
      <NumpadModal
        open={showNumpad}
        onClose={() => {
          setShowNumpad(false);
          setSelectedQuickSale(null);
          setTimeout(() => scanInputRef.current?.focus(), 100);
        }}
        onSubmit={handleQuickSaleSubmit}
        categoryName={selectedQuickSale?.name || ""}
        categoryColor={selectedQuickSale?.color || "#3B82F6"}
      />

      {/* Held Transactions Dialog */}
      <Dialog open={showHoldDialog} onOpenChange={(open) => {
        setShowHoldDialog(open);
        if (!open) {
          setTimeout(() => scanInputRef.current?.focus(), 100);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Held Transactions ({heldTransactions.length})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {heldTransactions.map((transaction, index) => (
              <div
                key={transaction.id}
                className="bg-zinc-50 border border-zinc-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                      #{index + 1}
                    </Badge>
                    <span className="text-sm text-zinc-500">
                      {formatHoldTime(transaction.timestamp)}
                    </span>
                  </div>
                  <span className="text-lg font-bold text-blue-600">
                    ${getHoldTotal(transaction.items).toFixed(2)}
                  </span>
                </div>
                <div className="text-sm text-zinc-600 mb-3">
                  {transaction.items.length} item(s): {transaction.items.map(i => i.product_name).join(", ").substring(0, 50)}
                  {transaction.items.map(i => i.product_name).join(", ").length > 50 ? "..." : ""}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => retrieveHeldTransaction(transaction.id)}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                    size="sm"
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Retrieve
                  </Button>
                  <Button
                    onClick={() => deleteHeldTransaction(transaction.id)}
                    variant="outline"
                    className="border-red-200 text-red-600 hover:bg-red-50"
                    size="sm"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ask For Price Dialog */}
      <Dialog open={showPriceInput} onOpenChange={(open) => {
        if (!open) {
          setShowPriceInput(false);
          setPriceInputProduct(null);
          setTimeout(() => scanInputRef.current?.focus(), 100);
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Enter Price — {priceInputProduct?.name}
              {priceInputProduct?.sku && <span className="text-sm font-normal text-zinc-500 ml-1">(SKU: {priceInputProduct.sku})</span>}
            </DialogTitle>
          </DialogHeader>
          <div>
            <PriceInputField 
              onSubmit={addToCartWithPrice}
              onCancel={() => {
                setShowPriceInput(false);
                setPriceInputProduct(null);
                setTimeout(() => scanInputRef.current?.focus(), 100);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Item Not Found Modal — closable ONLY via explicit button click.
          Scanner-injected Enter/Space and Escape/outside-click are all blocked so a rapid next
          barcode scan can't dismiss the alert. */}
      <Dialog open={showItemNotFound} onOpenChange={() => { /* no-op: forced modal */ }}>
        <DialogContent
          className="max-w-sm"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onKeyDownCapture={(e) => {
            // Scanner sends barcode chars followed by Enter — swallow Enter/Space at capture
            // phase so the auto-focused button click is NOT triggered by the scanner.
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          data-testid="item-not-found-dialog"
        >
          <div className="flex flex-col items-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-2">ITEM NOT FOUND</h2>
            <p className="text-zinc-500 text-sm mb-1">SKU: <span className="font-mono font-bold">{notFoundSku}</span></p>
            <p className="text-zinc-400 text-xs mb-6">This item does not exist in the system</p>
            <div className="flex gap-3 w-full">
              <Button
                variant="outline"
                type="button"
                className="flex-1"
                onClick={() => { setShowItemNotFound(false); setTimeout(() => scanInputRef.current?.focus(), 100); }}
                data-testid="item-not-found-ok"
              >
                OK
              </Button>
              <Button
                type="button"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={openQuickAdd}
                data-testid="item-not-found-add"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Add Item Modal */}
      <QuickAddDialog
        open={showQuickAdd}
        onOpenChange={setShowQuickAdd}
        quickAddForm={quickAddForm}
        handleQuickAddChange={handleQuickAddChange}
        handleQuickAddCategoryChange={handleQuickAddCategoryChange}
        handleQuickAddSubmit={handleQuickAddSubmit}
        categories={categories}
        subCategories={subCategories}
        groups={groups}
        manufacturers={manufacturers}
        vendors={vendors}
      />

      {/* Lookup Dialog */}
      <LookupDialog
        open={showLookup}
        onOpenChange={setShowLookup}
        products={products}
        onAddToCart={addToCart}
      />

      {/* Price Check Dialog */}
      <PriceCheckDialog
        open={showPriceCheck}
        onOpenChange={setShowPriceCheck}
        products={products}
        categories={categories}
        subCategories={subCategories}
        groups={groups}
        vendors={vendors}
        onCategoriesChange={setCategories}
        onSubCategoriesChange={setSubCategories}
        onGroupsChange={setGroups}
        onVendorsChange={setVendors}
        onProductsChange={fetchProducts}
      />


      {/* Sub-Button Popup */}
      {subButtonPopup && (
        <SubButtonPopup
          parentProduct={subButtonPopup.parentProduct}
          subButtons={subButtonPopup.subButtons}
          onAddToCart={addToCart}
          onClose={() => setSubButtonPopup(null)}
          displaySettings={displaySettings}
          allProducts={products}
        />
      )}


      {/* Price Change Dialog */}
      <Dialog open={showPriceChangeDialog} onOpenChange={(open) => {
        setShowPriceChangeDialog(open);
        if (!open) {
          setNewPriceInput("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Pencil className="w-5 h-5 text-orange-500" />
              Price Change
            </DialogTitle>
          </DialogHeader>
          
          {selectedCartItemId && (() => {
            const item = cart.find(i => i.product_id === selectedCartItemId);
            if (!item) return null;
            
            return (
              <div className="space-y-4 py-4">
                {/* Product Info */}
                <div className="bg-zinc-50 rounded-lg p-4 border">
                  <p className="font-bold text-lg text-zinc-900">{item.product_name}</p>
                  <p className="text-sm text-zinc-500">SKU: {item.sku || 'N/A'}</p>
                  <p className="text-sm text-zinc-600 mt-2">
                    Current Price: <span className="font-mono font-bold">${item.originalPrice?.toFixed(2) || item.price.toFixed(2)}</span>
                  </p>
                </div>
                
                {/* New Price Input */}
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-2">New Price</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-lg">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newPriceInput}
                      onChange={(e) => setNewPriceInput(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 text-xl font-mono font-bold border border-zinc-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                      placeholder="0.00"
                      autoFocus
                    />
                  </div>
                </div>
                
                {/* Info Text */}
                <div className="text-xs text-zinc-500 bg-zinc-50 p-3 rounded-lg">
                  <p><strong>Discount Item:</strong> Change price for this transaction only</p>
                  <p className="mt-1"><strong>Change Price:</strong> Update price permanently for all future transactions</p>
                </div>
              </div>
            );
          })()}
          
          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowPriceChangeDialog(false);
                setNewPriceInput("");
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDiscountItem}
              className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
            >
              Discount Item
            </Button>
            <Button
              onClick={handlePermanentPriceChange}
              className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
            >
              Change Price
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Vendor Payout Dialog */}
      <VendorPayoutDialog
        open={showVendorPayoutDialog}
        onOpenChange={setShowVendorPayoutDialog}
        onComplete={() => {
          // Optionally refresh data or show confirmation
        }}
      />
    </div>
  );
}

// Price Input Field Component - Calculator Style
// Numpad: cents-based (typing "568" = $5.68). Keyboard: supports "." for dollar entry (typing "5." Enter = $5.00)
function PriceInputField({ onSubmit, onCancel }) {
  const [inputStr, setInputStr] = useState(""); // String-based input
  const containerRef = useRef(null);

  // Quick preset amounts
  const quickAmounts = [
    1.00, 1.25, 1.50, 2.00, 2.50, 3.00, 3.50, 4.00,
    5.00, 6.00, 7.00, 8.00, 9.00, 10.00, 15.00, 20.00,
  ];

  const hasDot = inputStr.includes(".");

  // Get display value as formatted string
  const getDisplayValue = () => {
    if (!inputStr) return "0.00";
    if (hasDot) return (parseFloat(inputStr) || 0).toFixed(2);
    return ((parseInt(inputStr) || 0) / 100).toFixed(2);
  };

  // Get dollar value as number
  const getDollarValue = () => {
    if (!inputStr) return 0;
    if (hasDot) return parseFloat(inputStr) || 0;
    return (parseInt(inputStr) || 0) / 100;
  };

  // Handle numpad button clicks - append digit to string
  const handleNumpadClick = (digit) => {
    setInputStr(prev => {
      if (prev.includes(".")) {
        const parts = prev.split(".");
        if (parts[1].length >= 2) return prev; // max 2 decimal places
      }
      const newStr = prev + digit;
      if (newStr.includes(".")) {
        if (parseFloat(newStr) > 9999.99) return prev;
      } else {
        if (parseInt(newStr) > 999999) return prev;
      }
      return newStr;
    });
  };

  // Handle backspace - remove last character
  const handleBackspace = () => {
    setInputStr(prev => prev.slice(0, -1));
  };

  // Handle clear
  const handleClear = () => {
    setInputStr("");
  };


  const handleSubmit = () => {
    const price = getDollarValue();
    if (price > 0) {
      onSubmit(price);
    }
  };

  // Handle keyboard input (stable: always sees the latest handlers)
  const onKey = useStableCallback((e) => {
    if (!containerRef.current) return;
    
    if (e.key >= '0' && e.key <= '9') {
      e.preventDefault();
      handleNumpadClick(parseInt(e.key));
    } else if (e.key === '.') {
      e.preventDefault();
      setInputStr(prev => prev.includes(".") ? prev : (prev || "0") + ".");
    } else if (e.key === 'Backspace') {
      e.preventDefault();
      handleBackspace();
    } else if (e.key === 'Delete' || e.key === 'Escape') {
      if (e.key === 'Delete') {
        e.preventDefault();
        handleClear();
      } else {
        onCancel();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  });

  useEffect(() => {
    const handleKeyDown = (e) => onKey(e);
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onKey]);

  const numpadButtons = [
    { label: '1', action: () => handleNumpadClick(1) },
    { label: '2', action: () => handleNumpadClick(2) },
    { label: '3', action: () => handleNumpadClick(3) },
    { label: '4', action: () => handleNumpadClick(4) },
    { label: '5', action: () => handleNumpadClick(5) },
    { label: '6', action: () => handleNumpadClick(6) },
    { label: '7', action: () => handleNumpadClick(7) },
    { label: '8', action: () => handleNumpadClick(8) },
    { label: '9', action: () => handleNumpadClick(9) },
    { label: 'C', action: handleClear, className: 'bg-amber-100 hover:bg-amber-200 text-amber-700' },
    { label: '0', action: () => handleNumpadClick(0) },
    { label: '⌫', action: handleBackspace, className: 'bg-red-100 hover:bg-red-200 text-red-700' },
  ];

  return (
    <div ref={containerRef}>
      {/* Side-by-side: Numpad area + Quick Keys */}
      <div className="flex gap-3">
        {/* Left: Price display + Numpad + Buttons */}
        <div className="space-y-3 flex-1">
          {/* Price Display */}
          <div className="bg-zinc-900 rounded-lg p-4 text-right">
            <span className="text-3xl font-mono font-bold text-green-400" data-testid="price-display">
              ${getDisplayValue()}
            </span>
          </div>

          {/* Numpad Grid */}
          <div className="grid grid-cols-3 gap-2">
            {numpadButtons.map((btn) => (
              <Button
                key={btn.label}
                type="button"
                onClick={btn.action}
                className={`h-14 text-2xl font-bold ${btn.className || 'bg-zinc-100 hover:bg-zinc-200 text-zinc-900'}`}
                variant="outline"
                data-testid={`numpad-${btn.label}`}
              >
                {btn.label}
              </Button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-1">
            <Button onClick={onCancel} variant="outline" className="flex-1 h-12">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white text-lg font-bold"
              disabled={getDollarValue() <= 0}
              data-testid="price-input-submit"
            >
              Add to Cart
            </Button>
          </div>
        </div>

        {/* Right: Quick Amount Keys — full height */}
        <div className="border border-red-300 rounded-lg p-1.5 flex-shrink-0" style={{ width: '190px' }}>
          <div className="grid grid-cols-2 gap-1 h-full" style={{ gridAutoRows: '1fr' }}>
            {quickAmounts.map((amt) => (
              <Button
                key={amt}
                type="button"
                onClick={() => onSubmit(amt)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold border border-red-700"
                style={{ fontSize: '17px' }}
                data-testid={`quick-amt-${amt}`}
              >
                ${amt % 1 === 0 ? amt.toFixed(0) : amt.toFixed(2)}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
